import { Button, DatePicker, Form, FormInstance, Input, Select } from 'antd';
import { Batch } from '../../../../shared/VolunteerApplicationServiceTypes';
import LeftArrowWhite from '../../../../assets/images/arrow-left-white.svg';
import { useTranslation } from 'react-i18next';
import { DATE_DD_MMM_YYYY } from '../../../../shared/Constants';
import dayjs, { Dayjs, extend } from 'dayjs';
import { useCallback, useEffect, useRef, useState } from 'react';
import Icon from '../../../../components/ui-components/Icon';
import TextArea from 'antd/lib/input/TextArea';
import isBetween from 'dayjs/plugin/isBetween';
import moment from 'moment';

extend(isBetween);

interface BatchDetailsFormProps {
	form: FormInstance;
	batch?: Batch;
	activeBatch?: Batch;
	isEditForm?: boolean;
	onAddEditBatchSubmit?: () => void;
	onPublishBatch?: () => void;
	onCancelClick?: () => void;
	submitButtonDisabled?: boolean;
	viewBatchForm?: boolean;
}

export const BatchDetailsForm: React.FC<BatchDetailsFormProps> = ({
	form,
	isEditForm,
	onCancelClick,
	submitButtonDisabled,
	onAddEditBatchSubmit,
	viewBatchForm,
	batch,
	onPublishBatch,
	activeBatch,
}: BatchDetailsFormProps) => {
	const { t } = useTranslation();
	const [statusValue, setStatusValue] = useState<boolean | undefined>();

	const handleStatusChange = useCallback((value: boolean) => {
		setStatusValue(value);
	}, []);

	const isOverlapping = useCallback(
		(newStart: Dayjs, newEnd: Dayjs) => {
			const start = dayjs(activeBatch?.startDate);
			const snpCandidateResultDate = activeBatch?.snpResultDateForCandidate;
			const preworkEndDate = moment(snpCandidateResultDate)
				.add(89, 'days')
				.toString();
			const end = dayjs(preworkEndDate);
			return (
				newStart.isBetween(start, end, 'day', '[]') ||
				newEnd.isBetween(start, end, 'day', '[]') ||
				(newStart.isBefore(start) && newEnd.isAfter(end))
			);
		},
		[activeBatch]
	);

	const initialStartDateRef = useRef<Dayjs | null>(null);
	const startDateWatch = Form.useWatch('startDate', form) as Dayjs | null;

	useEffect(() => {
		if (!initialStartDateRef.current && isEditForm && startDateWatch) {
			initialStartDateRef.current = startDateWatch.startOf('day');
		}
	}, [isEditForm, startDateWatch]);

	const disabledStartDate = (current: dayjs.Dayjs) => {
		if (!current) return false;
		const min = initialStartDateRef.current ?? dayjs().startOf('day');

		return current.isBefore(min, 'day');
	};

	const startDateOnChange = (date: dayjs.Dayjs) => {
		const endDate = form.getFieldValue('endDate') as Dayjs | undefined;
		if (date && endDate && date.isAfter(endDate, 'day')) {
			form.setFieldsValue({ endDate: undefined });
		}
	};

	const disabledEndDate = (current: dayjs.Dayjs) => {
		const startDate = form.getFieldValue('startDate') as dayjs.Dayjs;
		if (!startDate) return true;
		return current && current.isBefore(dayjs(startDate).add(1, 'day'), 'day');
	};

	return (
		<Form
			name="basic"
			layout="vertical"
			size="middle"
			labelCol={{ span: 16 }}
			initialValues={{ remember: true }}
			autoComplete="off"
			form={form}
		>
			<div className="relative flex items-center justify-center border-b-[2px] border-gray-700 pb-6 mt-4 md:w-full md:max-w-4xl mx-auto">
				<div className="hidden md:block">
					<Button
						type="primary"
						onClick={onCancelClick}
						className="absolute left-0 top-0 font-bold text-sm flex items-center gap-2"
					>
						<Icon src={LeftArrowWhite} width="w-4" height="h-4" />
						{t('back')}
					</Button>
				</div>

				<div className="text-center font-sans font-bold text-lg">
					{viewBatchForm
						? t('viewBatch')
						: isEditForm
						? t('editBatch')
						: t('addBatch')}
				</div>
			</div>
			<div className="flex sm:justify-left md:justify-center md:items-left">
				<div className="bg-transparent md:bg-white sm:p-0 md:p-8 sm:rounded-none md:rounded-lg sm:border-none md:border md:border-solid md:border-gray-200 md:w-full md:max-w-4xl">
					<div className="flex flex-wrap overflow-y-auto pb-[80px] md:pb-[10px]">
						<div
							className={`w-full md:w-1/2 px-2 ${
								!isEditForm ? 'mb-0' : 'mb-4'
							}`}
						>
							<Form.Item
								label={
									<span className="text-gray-650 font-sans">
										{t('batchName')}
										<span className="text-red"> *</span>
									</span>
								}
								name="name"
								rules={[
									{
										required: true,
										message: <span>{t('required')}</span>,
									},
								]}
								required={false}
								className="mb-[16px]"
							>
								<Input
									maxLength={20}
									className="flex min-w-[200px]"
									size="large"
									disabled={viewBatchForm}
								/>
							</Form.Item>
						</div>
						<div className="w-full md:w-1/2 px-2 mb-4">
							<Form.Item
								label={
									<span className="text-gray-650 font-sans">
										{t('batchDescription')}
									</span>
								}
								name="description"
								className="mb-[16px]"
							>
								<TextArea
									maxLength={100}
									className="flex min-w-[200px] !h-[100px]"
									size="large"
									disabled={viewBatchForm}
								/>
							</Form.Item>
						</div>
						<div className="w-full md:w-1/2 px-2 mb-4">
							<Form.Item
								className="mb-[16px]"
								label={
									<span className="text-gray-650 font-sans">
										{t('batchStartDate')}
										<span className="text-red"> *</span>
									</span>
								}
								name="startDate"
								rules={[
									{ required: true, message: 'Please select start date' },
									({ getFieldValue }) => ({
										validator(_, value: Dayjs) {
											const startDate = getFieldValue('startDate') as
												| Dayjs
												| undefined;
											if (
												value &&
												startDate &&
												!isEditForm && // Need to check the condition for create batch
												isOverlapping(startDate, value)
											) {
												return Promise.reject(
													new Error(
														'Selected range overlaps with an existing batch'
													)
												);
											}
											return Promise.resolve();
										},
									}),
								]}
								required={false}
							>
								<DatePicker
									className="w-full"
									size="large"
									placeholder=""
									format={DATE_DD_MMM_YYYY}
									disabledDate={disabledStartDate}
									onChange={startDateOnChange}
									disabled={
										viewBatchForm ||
										(isEditForm &&
											(batch?.status === 'INACTIVE' ||
												batch?.status === 'ACTIVE'))
									}
								/>
							</Form.Item>
						</div>

						<div className="w-full md:w-1/2 px-2 mb-4">
							<Form.Item
								className="mb-4"
								label={
									<span className="text-gray-650 font-sans">
										{t('batchEndDate')}
										<span className="text-red"> *</span>
									</span>
								}
								name="endDate"
								rules={[
									{
										required: true,
										message: 'Please select end date',
									},
									({ getFieldValue }) => ({
										validator(_, value: Dayjs) {
											const endDate = getFieldValue('endDate') as
												| Dayjs
												| undefined;

											if (
												value &&
												endDate &&
												!isEditForm && // Need to check the condition for create batch
												isOverlapping(endDate, value)
											) {
												return Promise.reject(
													new Error(
														'Selected range overlaps with an existing batch'
													)
												);
											}

											return Promise.resolve();
										},
									}),
								]}
								required={false}
								dependencies={['startDate']}
							>
								<DatePicker
									className="w-full"
									size="large"
									placeholder=""
									format={DATE_DD_MMM_YYYY}
									disabledDate={disabledEndDate}
									disabled={
										viewBatchForm ||
										(isEditForm && batch?.status === 'INACTIVE')
									}
								/>
							</Form.Item>
						</div>
						{(isEditForm || viewBatchForm) && (
							<div className="w-full md:w-1/2 px-2 mb-4">
								<Form.Item
									label={
										<span className="text-gray-650 font-sans">
											{t('status')}
											<span className="text-red"> *</span>
										</span>
									}
									name="status"
									className="mb-[16px] font-sans"
								>
									<Select
										onChange={handleStatusChange}
										value={statusValue}
										options={[
											{ value: 'ACTIVE', label: `${t('active')}` },
											{ value: 'INACTIVE', label: `${t('inactive')}` },
											{ value: 'IN_DRAFT', label: `${t('draft')}` },
										]}
										className="flex min-w-[200px]"
										size="large"
										disabled={true}
									/>
								</Form.Item>
							</div>
						)}
					</div>
					<div className="flex flex-row py-[24px] px-[16px] fixed md:relative bottom-0 left-0 right-0 md:p-[8px] justify-center md:justify-end bg-white md:bg-transparent mt-[16px] md:mt-[12px] mb-[8px] md:mb-[0px] mobile-top-shadow">
						<Button
							onClick={onCancelClick}
							className="w-[180px] h-[36px] md:w-[140px] border-solid border-primary-blue text-primary-blue"
							variant="outlined"
						>
							{t('cancel')}
						</Button>
						{batch?.status === 'IN_DRAFT' && (
							<Button
								onClick={onPublishBatch}
								type="primary"
								disabled={submitButtonDisabled || viewBatchForm}
								className="w-[180px] h-[36px] md:w-[140px] ml-[16px]"
							>
								{t('publishBatch')}
							</Button>
						)}
						<Button
							onClick={onAddEditBatchSubmit}
							type="primary"
							disabled={submitButtonDisabled || viewBatchForm}
							className="w-[180px] h-[36px] md:w-[140px] ml-[16px]"
						>
							{t('submit')}
						</Button>
					</div>
				</div>
			</div>
		</Form>
	);
};
